export const PRODUCT_UPDATE_ITEMS_PER_PAGE = 3;

// Banner used when none was chosen, the same in the picker and the announcement: a single
// common image becomes the banner; two images stay together under the TransIA banner.
export function automaticCover(image, itemIds) {
  if (!image || image.additional_images?.length) return "banner";
  return !image.item_ids?.length || itemIds.every(id => image.item_ids.includes(id)) ? "image" : "banner";
}

// An image generated for only some items is shown beside the first of them,
// not above the whole publication.
export function attachLinkedImage(content) {
  const image = content?.image, linked = image?.item_ids;
  if (!linked?.length) return content;
  const items = (content.sections || []).flatMap(section => section.items);
  if (items.every(item => linked.includes(item.item_id))) return content;
  const owner = items.find(item => linked.includes(item.item_id));
  // Without its items (or beside an item image), the image stays common rather than disappearing.
  if (!owner || owner.image) return content;
  return { ...content, image: undefined, sections: content.sections.map(section => ({ ...section,
    items: section.items.map(item => item === owner ? { ...item, image } : item) })) };
}

// Publications not yet closed follow one another in one announcement. Each common
// image stays beside the first item of its own publication, not on every page.
export function mergeAnnouncementContents(contents) {
  if (contents.length < 2) return contents[0];
  const sections = contents.flatMap(content => {
    const { image, sections: own = [] } = attachLinkedImage(content);
    const owner = image && own.flatMap(section => section.items).find(item => !item.image);
    return owner ? own.map(section => ({ ...section, items: section.items.map(item => item === owner ? { ...item, image: { ...image, item_ids: undefined } } : item) })) : own;
  });
  return { ...contents.at(-1), image: undefined, sections };
}

// Same reduction as the server (resent_content): a message shows only its chosen items.
export function messageContent(content, itemIds) {
  const ids = new Set(itemIds.map(String));
  const result = { ...content, sections: (content?.sections || []).map(section => ({ ...section, items: section.items.filter(item => ids.has(String(item.item_id))) }))
    .filter(section => section.items.length) };
  if (content?.image?.item_ids?.length && !content.image.item_ids.some(id => ids.has(String(id)))) delete result.image;
  if ((result.cover != null && !["banner", "image"].includes(result.cover) && !ids.has(result.cover)) || (result.cover === "image" && !result.image)) delete result.cover;
  return result;
}

const messageRank = value => value ?? Infinity;

// Same order as the server once deployed: chosen order, then Journal order.
// Messages already committed have left with an earlier deployment.
export function pendingMessages(publications, committedIds = []) {
  return publications.filter(publication => publication.status === "published").flatMap(publication => (publication.resends_json || [])
    .filter(entry => entry.state === "prepared" && !committedIds.includes(entry.announcement_id)).map(entry => ({ ...entry, publication, content: messageContent(publication.content_json, entry.item_ids) })))
    .sort((a, b) => messageRank(a.order) - messageRank(b.order) || Date.parse(a.publication.published_at) - Date.parse(b.publication.published_at)
      || (a.announcement_id < b.announcement_id ? -1 : 1));
}

// Keep subjects and item order while bounding each announcement page.
export function productUpdateAnnouncementPages(content) {
  const pages = [];
  let count = 0;
  for (const section of content?.sections || []) {
    for (let offset = 0; offset < section.items.length; offset += PRODUCT_UPDATE_ITEMS_PER_PAGE) {
      const items = section.items.slice(offset, offset + PRODUCT_UPDATE_ITEMS_PER_PAGE);
      const start = count + 1;
      count += items.length;
      pages.push({ subject: section.subject, items, start, end: count });
    }
  }
  return pages;
}
